import { ChatBubbleBottomCenterTextIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input } from "@material-tailwind/react";
import { AuthContext } from "context/appContext";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { useContext, useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { BsFillSendArrowUpFill } from "react-icons/bs";
import { MdAttachFile, MdSend, MdSupportAgent } from "react-icons/md";
import { getConversations } from "redux/features/conversation/conversationSlice";
import Avatar from "shared/Avatar/Avatar";
import Badge from "shared/Badge/Badge";
import { socket } from "utils/socket";
import { z } from "zod";

const MAX_FILE_SIZE = 1024 * 1024 * 10;

const ACCEPTED_IMAGE_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];

const formSchema = z.object({
  msg: z.string().optional(),
  image: z
    .optional(z.instanceof(File))
    .refine(
      (file) => !file || (file?.size <= MAX_FILE_SIZE && ACCEPTED_IMAGE_MIME_TYPES.includes(file?.type)),
      "Le fichier doit être une image au format JPEG, PNG ou GIF et ne pas dépasser 10MB."
    ),
  image_name: z.string().optional(),
  image_type: z.string().optional(),
});

const ClientMessages = () => {
  const dispatch = useAppDispatch();
  const conversations = useAppSelector((state) => state.conversation?.conversations);
  const unreadConversationsCount = useAppSelector((state) => state.conversation.unreadConversationsCount);
  const [messages, setMessages] = useState<any[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<any | null>(null);
  const [assistantMessages, setAssistantMessages] = useState<any[]>([]);
  const [isAssistantMode, setIsAssistantMode] = useState(false);
  const authContext = useContext(AuthContext);
  const user = authContext?.user?.id;
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [filter, setFilter] = useState("");

  // Form handling
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { msg: "" },
    mode: "onSubmit",
  });

  useEffect(() => {
    dispatch(getConversations(filter));

    const handleNewMessage = (newMessage: any) => {
      // Handle assistant messages
      if (newMessage.receiverId === 1 || newMessage.senderId === 1) {
        if (
          (newMessage.senderId === user && newMessage.receiverId === 1) ||
          (newMessage.senderId === 1 && newMessage.receiverId === user)
        ) {
          setAssistantMessages((prevMessages) => [...prevMessages, newMessage]);
        }
        return;
      }

      // Handle regular conversation messages
      if (selectedConversation && !isAssistantMode) {
        const { user1_id, user2_id } = selectedConversation;

        const isMessageForUser =
          (newMessage.senderId === user &&
            (newMessage.receiverId === user1_id || newMessage.receiverId === user2_id)) ||
          (newMessage.receiverId === user &&
            (newMessage.senderId === user1_id || newMessage.senderId === user2_id));

        if (isMessageForUser) {
          setMessages((prevMessages) => [...prevMessages, newMessage]);
        }
      }
    };

    const handleConversationUpdated = () => {
      dispatch(getConversations(filter));
    };

    const handleMessagesLoaded = (loadedMessages: any) => {
      if (isAssistantMode) {
        setAssistantMessages(loadedMessages);
      } else {
        setMessages(loadedMessages);
      }
    };

    socket.on("new_message", handleNewMessage);
    socket.on("conversation_updated", handleConversationUpdated);
    socket.on("messages_loaded", handleMessagesLoaded);

    return () => {
      socket.off("new_message", handleNewMessage);
      socket.off("conversation_updated", handleConversationUpdated);
      socket.off("messages_loaded", handleMessagesLoaded);
    };
  }, [selectedConversation, user, socket, filter, isAssistantMode]);

  const markMessagesAsRead = () => {
    const currentMessages = isAssistantMode ? assistantMessages : messages;

    if (currentMessages.length > 0) {
      currentMessages.forEach((msg) => {
        if (!msg.isRead && msg.receiverId === user) {
          socket.emit("mark_as_read", {
            messageId: msg.id,
            receiverId: user,
            senderId: msg.senderId,
          });
        }
      });
    }
  };

  useEffect(() => {
    const currentMessages = isAssistantMode ? assistantMessages : messages;
    if (currentMessages.length > 0) {
      markMessagesAsRead();
    }
  }, [selectedConversation, messages, assistantMessages, user, socket, isAssistantMode]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages, assistantMessages, socket]);

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
    setIsLoading(true);

    if (isAssistantMode) {
      // Send message to assistant (receiverId = 1)
      socket.emit("send_messages", {
        senderId: user,
        receiverId: 1,
        content: data.msg,
        file: data.image,
        name: data.image_name,
        type: data.image_type,
      });

      // Optimistically update the UI for assistant
      const newMessage = {
        id: Date.now(),
        senderId: user,
        receiverId: 1,
        content: data.msg,
        fileUrl: data.image ? URL.createObjectURL(data.image) : null,
        createdAt: new Date(),
        isRead: false,
      };
      setAssistantMessages((prevMessages) => [...prevMessages, newMessage]);
    } else if (selectedConversation) {
      // Regular conversation logic
      const receiverId =
        selectedConversation.user1_id === user ? selectedConversation.user2_id : selectedConversation.user1_id;

      socket.emit("send_messages", {
        senderId: user,
        receiverId,
        content: data.msg,
        file: data.image,
        name: data.image_name,
        type: data.image_type,
      });

      // Optimistically update the UI
      const newMessage = {
        id: Date.now(),
        senderId: user,
        receiverId,
        content: data.msg,
        fileUrl: data.image ? URL.createObjectURL(data.image) : null,
        createdAt: new Date(),
        isRead: false,
      };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
    }

    reset();
    setIsLoading(false);
  };

  const handleConversationClick = (item: any) => {
    setSelectedConversation(item);
    setIsAssistantMode(false);
    const receiverId = item.user1_id === user ? item.user2_id : item.user1_id;
    socket.emit("load_messages", { senderId: user, receiverId });
  };

  const handleAssistantClick = () => {
    setSelectedConversation(null);
    setIsAssistantMode(true);
    // Load assistant messages
    socket.emit("load_messages", { senderId: user, receiverId: 1 });
  };

  const messagesList = () => {
    return (
      <div className="flex flex-col h-full border-r bg-gradient-to-b from-blue-50 to-blue-100/50 border-blue-200/50">
        {/* Header */}
        <div className="p-6 border-b bg-white/70 backdrop-blur-sm border-blue-200/30">
          <h2 className="mb-4 text-xl font-bold text-gray-800">Messages</h2>
          <div className="flex gap-3">
            <Badge
              name="Tous"
              color={filter === "" && !isAssistantMode ? "black" : undefined}
              onClick={() => {
                setFilter("");
                setIsAssistantMode(false);
              }}
              className="px-4 py-2 transition-all duration-200 rounded-full shadow-sm cursor-pointer hover:scale-105"
            />
            <Badge
              name={`Non lus ${unreadConversationsCount > 0 ? `(${unreadConversationsCount})` : ""}`}
              color={filter === "unread" && !isAssistantMode ? "black" : undefined}
              onClick={() => {
                setFilter("unread");
                setIsAssistantMode(false);
              }}
              className="px-4 py-2 transition-all duration-200 rounded-full shadow-sm cursor-pointer hover:scale-105"
            />
            {authContext?.user?.id !== 1 && (
              <Badge
                name="Assistant"
                color={isAssistantMode ? "black" : undefined}
                onClick={handleAssistantClick}
                className="px-4 py-2 transition-all duration-200 rounded-full shadow-sm cursor-pointer hover:scale-105"
              />
            )}
          </div>
        </div>

        {/* Assistant Chat Option */}
        {isAssistantMode ? (
          <div className="flex-1 p-4">
            <div className="relative cursor-pointer rounded-xl p-4 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg bg-gradient-to-r from-eventToBee to-eventToBee/90 text-white shadow-xl">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/20 ring-2 ring-white/50">
                    <MdSupportAgent className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-semibold text-white">Assistant Client</h3>
                  </div>
                  <p className="text-xs text-white/90">Besoin d'aide? Contactez notre équipe de support</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Conversations List */
          <div className="flex-1 p-4 space-y-3 overflow-y-auto">
            {conversations && conversations.length !== 0 ? (
              conversations
                .filter((item: any) => {
                  // Only filter out if the current user is NOT user ID 1
                  if (authContext?.user?.id !== 1) {
                    return item.user1_id !== 1 && item.user2_id !== 1;
                  }
                  return true;
                })
                .map((item: any) => {
                  const otherUser = item.user1_id === user ? item?.user2 : item?.user1;
                  const isSelected = selectedConversation?.id === item.id;
                  const isUnread = !item.chats[0]?.isRead;

                  return (
                    <div
                      key={item.id}
                      className={`
            relative cursor-pointer rounded-xl p-4 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg
            ${
              isSelected
                ? "bg-gradient-to-r from-eventToBee to-eventToBee/90 text-white shadow-xl"
                : isUnread
                  ? "bg-gradient-to-r from-blue-100 to-blue-200/70 shadow-md"
                  : "bg-white/80 backdrop-blur-sm shadow-sm hover:bg-white/90"
            }
          `}
                      onClick={() => handleConversationClick(item)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <Avatar
                            imgUrl={
                              otherUser?.Clients[0]?.client_url
                                ? process.env.REACT_APP_CLOUD_FRONT + otherUser?.Clients[0]?.client_url
                                : ""
                            }
                            sizeClass="w-12 h-12 ring-2 ring-white/50"
                            userName={otherUser?.Clients[0]?.name}
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h3
                              className={`font-semibold text-sm truncate ${isSelected ? "text-white" : "text-gray-800"}`}
                            >
                              {otherUser?.Clients[0]?.name}
                            </h3>
                            {item.unreadCount > 0 && (
                              <div
                                className={`flex items-center justify-center min-w-[18px] h-5 px-2 text-xs font-bold rounded-full ${
                                  isSelected ? "bg-white/20 text-white" : "bg-red-500 text-white"
                                }`}
                              >
                                {item.unreadCount}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between">
                            <p
                              className={`text-xs truncate max-w-[140px] ${
                                isSelected ? "text-white/90" : "text-gray-600"
                              }`}
                            >
                              {item.chats[0]?.content && item.chats[0]?.content.startsWith("message/")
                                ? `${otherUser ? "fichier réçu" : "fichier envoyé"}`
                                : item.chats[0]?.content?.length > 15
                                  ? item.chats[0]?.content.substring(0, 15) + "..."
                                  : item.chats[0]?.content}
                            </p>
                            <span className={`text-xs ${isSelected ? "text-white/80" : "text-gray-500"}`}>
                              {item.chats[0]?.updatedAt
                                ? new Date(item.chats[0]?.updatedAt).toLocaleDateString("fr-FR", {
                                    month: "short",
                                    day: "2-digit",
                                  })
                                : null}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <div className="flex items-center justify-center w-16 h-16 mb-4 bg-blue-100 rounded-full">
                  <ChatBubbleBottomCenterTextIcon className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="mb-2 font-semibold text-gray-800">Aucun message</h3>
                <p className="text-xs text-gray-500 max-w-[200px]">
                  Lorsque vous recevez un nouveau message, il apparaîtra ici.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const messageContent = () => {
    const otherUser = isAssistantMode
      ? null
      : selectedConversation?.user1_id === user
        ? selectedConversation?.user2
        : selectedConversation?.user1;

    const currentMessages = isAssistantMode ? assistantMessages : messages;

    return (
      <div className="flex flex-col h-full bg-gradient-to-b from-gray-50 to-white">
        {selectedConversation || isAssistantMode ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b shadow-sm bg-white/90 backdrop-blur-sm border-gray-200/50">
              <div className="flex items-center space-x-4">
                {isAssistantMode ? (
                  <>
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full ring-2 ring-blue-100">
                      <MdSupportAgent className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">Assistant Client</h3>
                      <p className="text-sm text-gray-600">Notre équipe de support est là pour vous aider</p>
                    </div>
                  </>
                ) : (
                  otherUser && (
                    <>
                      <Avatar
                        imgUrl={
                          otherUser?.Clients[0]?.client_url
                            ? process.env.REACT_APP_CLOUD_FRONT + otherUser?.Clients[0]?.client_url
                            : ""
                        }
                        sizeClass="w-12 h-12 ring-2 ring-blue-100"
                        userName={otherUser?.Clients[0]?.name}
                      />
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">{otherUser?.Clients[0]?.name}</h3>
                      </div>
                    </>
                  )
                )}
              </div>
            </div>

            {/* Messages Container */}
            <div className="flex-1 p-4 space-y-4 overflow-y-auto" ref={messagesEndRef} style={{ height: "400px" }}>
              {currentMessages.map((msg, index) => {
                const isOwn = msg.senderId === user;
                return (
                  <div key={index} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] ${isOwn ? "order-2" : "order-1"}`}>
                      <div
                        className={`
                          relative px-4 py-3 rounded-2xl shadow-sm transition-all duration-200 hover:shadow-md
                          ${
                            isOwn
                              ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-br-md"
                              : isAssistantMode
                                ? "bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-bl-md"
                                : "bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-bl-md"
                          }
                        `}
                      >
                        {msg.content && msg.content.startsWith("message/") ? (
                          <div className="relative group">
                            <img
                              src={process.env.REACT_APP_CLOUD_FRONT + msg.content}
                              alt="message image"
                              className="object-cover w-40 h-40 transition-transform duration-200 rounded-lg group-hover:scale-105"
                            />
                            <div className="absolute inset-0 transition-all duration-200 rounded-lg bg-black/0 group-hover:bg-black/10"></div>
                          </div>
                        ) : (
                          <div className="text-sm leading-relaxed">
                            {msg.content.split("\n").map((line: any, index: any) => (
                              <p key={index} className="mb-1 last:mb-0">
                                {line}
                              </p>
                            ))}
                          </div>
                        )}

                        {msg.fileUrl && (
                          <div className="relative mt-3 group">
                            <img
                              src={msg.fileUrl}
                              alt="message image"
                              className="object-cover w-40 h-40 transition-transform duration-200 rounded-lg group-hover:scale-105"
                            />
                            <div className="absolute inset-0 transition-all duration-200 rounded-lg bg-black/0 group-hover:bg-black/10"></div>
                          </div>
                        )}
                      </div>

                      <div className={`mt-1 px-2 ${isOwn ? "text-right" : "text-left"}`}>
                        <span className="text-xs text-gray-500">
                          {new Date(msg.createdAt).toLocaleTimeString("fr-FR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Image Preview */}
            {getValues("image") && (
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-end">
                  <div className="relative group">
                    <img
                      src={URL.createObjectURL(getValues("image") as File)}
                      alt="file preview"
                      className="object-cover w-32 h-32 transition-transform duration-200 rounded-lg shadow-md group-hover:scale-105"
                    />
                    <div className="absolute inset-0 transition-all duration-200 rounded-lg bg-black/0 group-hover:bg-black/10"></div>
                  </div>
                </div>
              </div>
            )}

            {/* Message Input */}
            <div className="p-4 border-t bg-white/90 backdrop-blur-sm border-gray-200/50">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="relative flex items-center transition-all duration-200 border border-gray-200 rounded-full bg-gray-50 focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100">
                  <Input
                    type="text"
                    className="!border-0 !bg-transparent !outline-none !ring-0 focus:!ring-0 focus:!border-0 pl-6 pr-20"
                    {...register("msg")}
                    placeholder={isAssistantMode ? "Décrivez votre problème..." : "Saisissez votre message..."}
                  />

                  <div className="absolute flex items-center space-x-2 right-2">
                    <Button
                      size="sm"
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="!p-2 !rounded-full !bg-gray-200 hover:!bg-gray-300 !text-gray-600 !shadow-none transition-all duration-200 hover:scale-110"
                    >
                      <MdAttachFile className="w-4 h-4" />
                    </Button>

                    <Button
                      size="sm"
                      type="submit"
                      disabled={isLoading}
                      className="!p-2 !rounded-full !bg-blue-500 hover:!bg-blue-600 !text-white !shadow-md transition-all duration-200 hover:scale-110 disabled:hover:scale-100"
                    >
                      {isLoading ? (
                        <BsFillSendArrowUpFill className="w-4 h-4 animate-pulse" />
                      ) : (
                        <MdSend className="w-4 h-4" />
                      )}
                    </Button>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setValue("image", file);
                        setValue("image_name", file.name);
                        setValue("image_type", file.type);
                        trigger("image");
                        trigger("image_name");
                        trigger("image_type");
                      }
                    }}
                    accept="image/jpeg, image/jpg, image/png, image/webp"
                  />
                </div>
                {errors.msg?.message && (
                  <div className="px-2 mt-2 text-xs text-red-500">{errors.msg?.message}</div>
                )}
              </form>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center flex-1">
            <div className="py-12 text-center">
              <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 bg-blue-100 rounded-full">
                <ChatBubbleBottomCenterTextIcon className="w-10 h-10 text-blue-500" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-800">Sélectionnez une conversation</h3>
              <p className="max-w-sm text-gray-500">
                Choisissez une conversation dans la liste de gauche pour commencer à discuter.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full h-screen bg-gray-100 nc-ListingStayDetailPage">
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar: Conversation List */}
        <div className="flex-shrink-0 w-80">{messagesList()}</div>

        {/* Right Side: Message Content */}
        <div className="flex flex-col flex-1">{messageContent()}</div>
      </div>
    </div>
  );
};

export default ClientMessages;
